/** Shared cart token — cookie on `.ammachekku.com` so shop.ammachekku.com shares the cart */

import { getCurrentAuthToken } from './authToken';

const CART_TOKEN_KEY = 'cartToken';
const CART_COOKIE_NAME = 'ammachekku_cart_token';
const CART_EVENT_NAME = 'ammachekku-cart-changed';
const CART_COOKIE_LIFETIME = 60 * 60 * 24 * 180;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const getCookieDomain = () => {
  if (!isBrowser) return '';

  const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return '';
  }

  const parts = host.split('.');
  if (parts.length < 2) return host;
  return `.${parts.slice(-2).join('.')}`;
};

const readCookie = (name) => {
  if (!isBrowser) return '';

  const cookiePrefix = `${name}=`;
  const matched = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(cookiePrefix));

  if (!matched) return '';
  return decodeURIComponent(matched.slice(cookiePrefix.length));
};

const writeCookie = (token) => {
  if (!isBrowser) return;

  const domain = getCookieDomain();
  const segments = [
    `${CART_COOKIE_NAME}=${encodeURIComponent(token || '')}`,
    'path=/',
    'SameSite=Lax',
  ];

  if (domain) segments.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') segments.push('Secure');

  if (token) {
    segments.push(`max-age=${CART_COOKIE_LIFETIME}`);
  } else {
    segments.push('expires=Thu, 01 Jan 1970 00:00:00 GMT', 'max-age=0');
  }

  document.cookie = segments.join('; ');

  if (!token) {
    document.cookie = segments.filter((s) => !s.startsWith('domain=')).join('; ');
  }
};

const dispatchCartChanged = () => {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(CART_EVENT_NAME));
};

const generateCartToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const storeCartToken = (token) => {
  if (!isBrowser) return '';
  const normalized = String(token || '').trim();
  if (!normalized) return '';

  localStorage.setItem(CART_TOKEN_KEY, normalized);
  writeCookie(normalized);
  dispatchCartChanged();
  return normalized;
};

export const getSharedCartToken = () => readCookie(CART_COOKIE_NAME);

export const getCartToken = () => {
  if (!isBrowser) return '';

  const cookieToken = getSharedCartToken();
  if (cookieToken) {
    localStorage.setItem(CART_TOKEN_KEY, cookieToken);
    return cookieToken;
  }

  const stored = localStorage.getItem(CART_TOKEN_KEY);
  if (stored) {
    writeCookie(stored);
    return stored;
  }

  return storeCartToken(generateCartToken());
};

export const getAuthHeaders = () => {
  const headers = {
    Accept: 'application/json',
    'X-Cart-Token': getCartToken(),
  };

  const auth = getCurrentAuthToken();
  if (auth) {
    headers.Authorization = `Bearer ${auth}`;
  }

  return headers;
};

export const CART_EVENT = CART_EVENT_NAME;
